import { useParams } from "react-router-dom";

import { LoadingState } from "~/common/components";
import {
  CONSULTATIONS_INPUTS_TYPES,
  CONSULTATIONS_QUESTION_TYPES,
} from "~/common/utils";
import {
  PatientScreeningQuestionResponse,
  patientScreeningSectionTitle,
  SCREENING_QUESTION_CODES,
  SCREENING_QUESTION_TYPES,
  ScreeningQuestionAnswer,
  ScreeningQuestionData,
  screeningQuestionDataByCodes,
} from "~/common/utils/consultations/screening";
import {
  ConsultationFormLayout,
  ScreeningCompletedScreen,
  ScreeningQuestionRenderer,
} from "~/patients/components/consultations";
import {
  useAnswerQuestion,
  useGetCurrentPatient,
  useGetCurrentScreeningBundle,
  useGetCurrentScreeningQuestion,
  useGoBackOneQuestion,
} from "~/patients/hooks/queries";

const transformCurrentQuestion = (
  currentQuestion: PatientScreeningQuestionResponse,
) => {
  if (currentQuestion.is_custom) {
    if (
      currentQuestion.question_type ===
      SCREENING_QUESTION_TYPES.supplemental_free_text
    ) {
      return {
        ...currentQuestion,
        title: currentQuestion.question_text,
        type: CONSULTATIONS_QUESTION_TYPES.text,
        inputs: [
          {
            id: "1",
            placeholder: "Enter response",
            value: "supplemental_free_text_response",
            type: CONSULTATIONS_INPUTS_TYPES.text_area,
            required: true,
          },
        ],
      };
    }

    if (
      currentQuestion.question_type ===
      SCREENING_QUESTION_TYPES.supplemental_single_select
    ) {
      return {
        title: currentQuestion.question_text,
        type: SCREENING_QUESTION_TYPES.radio,
        options: currentQuestion.question_options.map((option) => option.text),
      };
    }

    if (
      currentQuestion.question_type ===
      SCREENING_QUESTION_TYPES.supplemental_multi_select
    ) {
      return {
        ...currentQuestion,
        title: currentQuestion.question_text,
        type: CONSULTATIONS_QUESTION_TYPES.multi_check,
        options: currentQuestion.question_options.map((option) => ({
          value: option.text,
          label: option.text,
        })),
      };
    }

    // safeguard - unhandled question type
    throw new Error(
      `Unhandled custom type: ${currentQuestion.question_type}, code: ${currentQuestion.code}, missing definition`,
    );
  }

  // safeguard - supplemental questions is_custom should be true
  if (currentQuestion.code === SCREENING_QUESTION_CODES.supplemental_question) {
    throw new Error(
      `Invalid custom type: ${currentQuestion.question_type}, code: ${currentQuestion.code}, is_custom should be true`,
    );
  }

  return screeningQuestionDataByCodes[currentQuestion.code];
};

export const ScreeningQuestion = () => {
  const { id } = useParams();

  const { data: patient } = useGetCurrentPatient();
  const consultationId = patient?.consultation?.id;

  const { data: currentQuestion, isLoading } = useGetCurrentScreeningQuestion(
    id,
    consultationId,
  );

  const { data: currentScreeningBundle } = useGetCurrentScreeningBundle();

  const { goBackOneQuestionMutation, isPending: isNavigatingBack } =
    useGoBackOneQuestion();
  const { answerQuestionMutation, isPending: isAnswering } =
    useAnswerQuestion();

  if (!id) return null;

  const handleGoBackFinalScreen = () => {
    goBackOneQuestionMutation({ id, consultationId });
  };

  if (isLoading) return <LoadingState className="h-screen-calculated" />;

  if (!currentQuestion || !currentScreeningBundle)
    return (
      <ScreeningCompletedScreen
        handleGoBackFinalScreen={handleGoBackFinalScreen}
        isNavigatingBack={isNavigatingBack}
      />
    );

  const { id: questionId, form: screening, answer, optional } = currentQuestion;

  const question = transformCurrentQuestion(currentQuestion);

  const title = patientScreeningSectionTitle[screening.code];
  const isFirstQuestion = questionId === screening.first_question_id;

  if (!question) return null;

  const onSubmit = (answer: ScreeningQuestionAnswer) =>
    answerQuestionMutation({ id, consultationId, answer });

  return (
    <ConsultationFormLayout
      currentSection={screening.order}
      title={title}
      totalSteps={currentScreeningBundle.total_sections}
    >
      <ScreeningQuestionRenderer
        key={currentQuestion.id}
        question={question as ScreeningQuestionData}
        handleGoBack={() => goBackOneQuestionMutation({ id, consultationId })}
        isOptional={optional}
        onSubmit={onSubmit}
        disableBack={isFirstQuestion}
        answer={answer}
        isSubmitting={isNavigatingBack || isAnswering}
      />
    </ConsultationFormLayout>
  );
};
